// Horarios del negocio configurables desde el panel de administración.
// Se cargan una sola vez por sesión desde /settings/business-hours;
// si el API falla se usan los valores históricos (11:00–15:00).

import { API_URL } from './api';

export const DEFAULT_BUSINESS_HOURS = {
  orderingOpens: '08:00',
  orderStart: '11:00',
  orderEnd: '15:00',
  deliveryEnabled: true,
  deliveryStart: '11:00',
  deliveryEnd: '15:00',
};

let cachedPromise = null;

const normalize = (raw) => ({
  orderingOpens: raw.ordering_opens || DEFAULT_BUSINESS_HOURS.orderingOpens,
  orderStart: raw.order_start || DEFAULT_BUSINESS_HOURS.orderStart,
  orderEnd: raw.order_end || DEFAULT_BUSINESS_HOURS.orderEnd,
  deliveryEnabled: Number(raw.delivery_enabled) === 1,
  deliveryStart: raw.delivery_start || DEFAULT_BUSINESS_HOURS.deliveryStart,
  deliveryEnd: raw.delivery_end || DEFAULT_BUSINESS_HOURS.deliveryEnd,
});

const fetchSettings = () =>
  fetch(`${API_URL}/settings/business-hours`)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((json) => {
      if (json.status === 'success' && json.data) return normalize(json.data);
      throw new Error('Unexpected settings response');
    })
    .catch((error) => {
      console.error('Error loading business hours, using defaults:', error);
      return DEFAULT_BUSINESS_HOURS;
    });

/**
 * Ventana de venta del día (business_settings): hasta orderEnd el sitio muestra
 * el menú de hoy y agenda para hoy; después pasa al día siguiente.
 * Es independiente de las horas de entrega (delivery_slots): esas solo llenan
 * el selector de hora, no mueven el corte del menú.
 */
export const loadBusinessHours = () => {
  if (!cachedPromise) {
    cachedPromise = fetchSettings();
  }
  return cachedPromise;
};

/**
 * Disponibilidad de slots de entrega para una fecha (sin caché: el cupo
 * restante cambia con cada pedido). configured=false => modo legacy
 * (selector libre cada 15 min dentro de la ventana de venta).
 */
export const fetchSlotAvailability = async (dateStr) => {
  try {
    const res = await fetch(`${API_URL}/orders/slot-availability?date=${dateStr}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.status === 'success' && json.data) return json.data;
    throw new Error('Unexpected slot availability response');
  } catch (error) {
    console.error('Error loading slot availability, falling back to legacy picker:', error);
    return { configured: false, slots: [] };
  }
};

/** 'HH:MM' -> minutos desde medianoche. */
export const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + (m || 0);
};

/** Hora actual en zona de México (la cocina opera en America/Mexico_City). */
export const mexicoNow = () =>
  new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));

/** Minutos desde medianoche de la hora actual en México. */
export const mexicoNowMinutes = () => {
  const now = mexicoNow();
  return now.getHours() * 60 + now.getMinutes();
};

/** Fecha de hoy en México como 'YYYY-MM-DD'. */
export const mexicoTodayString = () => {
  const now = mexicoNow();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/** Formatea 'HH:MM' como '11:00 AM' para mostrar al usuario. */
export const formatHourLabel = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayHour}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};
